import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
    SparklesIcon,
    LockClosedIcon,
    ChartBarIcon,
    BoltIcon,
    CheckCircleIcon,
    ShieldCheckIcon,
    ArrowRightIcon,
} from '@heroicons/react/24/outline';

import hexlinkCardRender from '../../assets/hexlink-card-render.png';

const features = [
    {
        name: 'Exclusive Hexlink Access',
        description: 'Your digital business card is exclusively available to Hexlink cardholders, ensuring a premium networking experience.',
        icon: LockClosedIcon,
        highlight: true,
    },
    {
        name: 'Physical Meets Digital',
        description: 'Seamlessly bridge the gap between traditional and modern networking with NFC-enabled Hexlink cards.',
        icon: BoltIcon,
    },
    {
        name: 'Instant Card Updates',
        description: 'Change jobs? New phone number? Update your digital card instantly without reprinting physical cards.',
        icon: SparklesIcon,
    },
    {
        name: 'View Tracking',
        description: 'See how many times your card has been viewed. Pro (coming soon) adds advanced analytics: when, where and how people tap.',
        icon: ChartBarIcon,
    },
    {
        name: 'Pay Once with Basic',
        description: 'The Basic plan is a one-time $10 setup with unlimited updates and lifetime access. No subscription required.',
        icon: CheckCircleIcon,
    },
    {
        name: 'Secure & Private',
        description: 'Your information is protected, and you decide exactly what is shared on your card.',
        icon: ShieldCheckIcon,
    },
];

const lineup = [
    {
        name: 'Hexlink Keychain',
        description: 'Always on your keys, always ready to tap.',
        price: '$7',
        href: 'https://nordic3d.us/product.html?handle=hexlink-dbc-keychain',
        cta: 'Shop keychain',
    },
    {
        name: 'Hexlink Card',
        description: 'The original 3D printed card in tough, weather-resistant ASA.',
        price: '$15',
        href: 'https://nordic3d.us/product.html?handle=hexlink-dbc',
        cta: 'Shop card',
    },
    {
        name: 'Hexlink Metal',
        description: 'Laser-engraved black stainless steel with a premium weight and glossy finish.',
        price: '$20',
        href: 'https://nordic3d.us/product.html?handle=hexlink-metal',
        cta: 'Shop metal card',
        badge: 'New',
    },
];

const benefits = [
    'NFC-enabled Hexlink card required for access',
    'One-time $10 setup on Basic - no subscription required',
    'Card view tracking',
    'Unlimited updates and modifications',
    'Professional QR codes for any situation',
    'Direct vCard downloads for contacts',
    'Mobile-optimized design',
];

// Small reusable label so the page matches nordic3d.us.
function Eyebrow({ children }) {
    return (
        <div className="font-techmono text-xs uppercase tracking-[0.14em] text-hx-accent mb-3">
            {children}
        </div>
    );
}

const fadeUp = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
};

function Home() {
    return (
        <>
            <Helmet>
                <title>Hexlink Digital Business Card | Nordic3D</title>
                <meta
                    name="description"
                    content="Get your NFC-enabled Hexlink card and unlock your digital business card with a one-time $10 setup. Pro, Team and Non-Profit plans available."
                />
            </Helmet>

            <div className="relative">
                {/* Hero */}
                <section className="relative overflow-hidden">
                    <div
                        className="absolute inset-0 opacity-60 pointer-events-none"
                        style={{
                            backgroundImage:
                                'linear-gradient(#1f2834 1px, transparent 1px), linear-gradient(90deg, #1f2834 1px, transparent 1px)',
                            backgroundSize: '56px 56px',
                            maskImage: 'radial-gradient(ellipse 70% 60% at 50% 30%, #000 20%, transparent 75%)',
                            WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 30%, #000 20%, transparent 75%)',
                        }}
                    />
                    <div className="absolute -top-40 right-[-10%] w-[520px] h-[520px] rounded-full bg-hx-accent opacity-25 blur-[100px] pointer-events-none" />
                    <div className="absolute bottom-[-220px] left-[-10%] w-[420px] h-[420px] rounded-full bg-hx-laser opacity-10 blur-[100px] pointer-events-none" />

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28">
                        <div className="text-center">
                            <motion.div {...fadeUp} transition={{ duration: 0.4 }} className="flex justify-center mb-6">
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-techmono text-xs text-hx-accent bg-hx-accent/10 border border-hx-accent/30">
                                    <span className="w-2 h-2 rounded-full bg-hx-accent shadow-[0_0_10px_#35d6ff]" />
                                    Exclusive to Hexlink Cardholders
                                </span>
                            </motion.div>

                            <motion.h1
                                {...fadeUp}
                                transition={{ duration: 0.4, delay: 0.05 }}
                                className="text-5xl font-bold tracking-tight text-hx-text sm:text-6xl md:text-7xl leading-[1.05]"
                            >
                                <span className="block">Your Hexlink.</span>
                                <span className="block bg-gradient-to-r from-hx-accent to-[#9af0ff] bg-clip-text text-transparent">
                                    Your Digital Identity.
                                </span>
                            </motion.h1>

                            <motion.p
                                {...fadeUp}
                                transition={{ duration: 0.4, delay: 0.1 }}
                                className="mt-6 max-w-3xl mx-auto text-lg sm:text-xl text-hx-muted leading-relaxed"
                            >
                                Join the exclusive network of professionals using Hexlink NFC cards.
                                Unlock your digital business card with a one-time $10 setup and
                                change how you connect.
                            </motion.p>

                            <motion.div
                                {...fadeUp}
                                transition={{ duration: 0.4, delay: 0.15 }}
                                className="mt-10 flex flex-col sm:flex-row gap-3 justify-center items-center"
                            >
                                <a
                                    href="https://nordic3d.us/hexlink.html"
                                    className="group inline-flex items-center px-7 py-3.5 rounded-xl text-base font-semibold text-hx-ink bg-hx-accent hover:bg-hx-accent2 hover:shadow-[0_0_30px_rgba(53,214,255,0.35)] transition-all"
                                >
                                    Get Your Hexlink Today
                                    <ArrowRightIcon className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </a>
                                <Link
                                    to="/login"
                                    className="inline-flex items-center px-7 py-3.5 rounded-xl text-base font-semibold text-hx-text border border-hx-line2 hover:border-hx-accent hover:text-hx-accent transition-colors"
                                >
                                    Member Login
                                </Link>
                            </motion.div>

                            <p className="mt-4 text-sm text-hx-dim">
                                Already have a Hexlink card? Login to activate your digital business card.
                            </p>
                        </div>

                        {/* Hero visual */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.97 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="mt-16 sm:mt-20 relative mx-auto max-w-5xl"
                        >
                            <div className="rounded-2xl p-px bg-gradient-to-br from-hx-accent/60 via-hx-line2 to-hx-laser/40 shadow-[0_40px_80px_rgba(0,0,0,0.5)]">
                                <div className="rounded-2xl bg-hx-panel p-6 sm:p-12">
                                    <div className="grid md:grid-cols-2 gap-8 items-center">
                                        <div className="relative rounded-xl bg-[radial-gradient(circle_at_50%_40%,#1a2430,#0b0f14)] p-6">
                                            <img
                                                src={hexlinkCardRender}
                                                alt="Hexlink NFC Business Card"
                                                className="w-full h-auto rounded-lg drop-shadow-2xl"
                                            />
                                        </div>

                                        {/* Digital card preview */}
                                        <div className="rounded-xl bg-hx-bg border border-hx-line p-6">
                                            <div className="flex items-center gap-4 mb-6">
                                                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-hx-accent to-hx-accent/30 shadow-[0_0_30px_rgba(53,214,255,0.3)] flex-none" />
                                                <div className="min-w-0">
                                                    <div className="h-4 bg-hx-line2 rounded w-32 mb-2" />
                                                    <div className="h-3 bg-hx-line rounded w-24" />
                                                </div>
                                            </div>
                                            <div className="space-y-3">
                                                <div className="h-3 bg-hx-line rounded" />
                                                <div className="h-3 bg-hx-line rounded w-5/6" />
                                                <div className="h-3 bg-hx-line rounded w-4/6" />
                                            </div>
                                            <div className="mt-6 flex gap-3">
                                                {[...Array(5)].map((_, i) => (
                                                    <div key={i} className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-hx-panel2 border border-hx-line" />
                                                ))}
                                            </div>
                                            <div className="mt-6 h-10 rounded-lg bg-hx-accent/90" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Value proposition */}
                <section className="py-16 sm:py-20 bg-hx-bg2 border-y border-hx-line">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <Eyebrow>Why Hexlink</Eyebrow>
                            <h2 className="text-3xl font-bold tracking-tight text-hx-text sm:text-4xl">
                                One Card. Unlimited Possibilities.
                            </h2>
                            <p className="mt-4 max-w-2xl mx-auto text-lg text-hx-muted">
                                Your Hexlink card is the key to professional networking in the digital age.
                            </p>
                        </div>

                        <div className="mt-12 grid sm:grid-cols-3 gap-4">
                            {[
                                { big: '$10', label: 'One-time setup', sub: 'Basic plan, no subscription', accent: true },
                                { big: '∞', label: 'Unlimited updates', sub: 'Change anytime' },
                                { big: '24/7', label: 'Always accessible', sub: 'Share anywhere', accent: true },
                            ].map((s) => (
                                <div key={s.label} className="rounded-2xl bg-hx-panel border border-hx-line p-8 text-center">
                                    <div className={`text-4xl font-bold tracking-tight ${s.accent ? 'text-hx-accent' : 'text-hx-text'}`}>{s.big}</div>
                                    <div className="mt-2 text-hx-text">{s.label}</div>
                                    <div className="mt-1 font-techmono text-xs uppercase tracking-wider text-hx-dim">{s.sub}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Features */}
                <section className="py-16 sm:py-24">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <Eyebrow>Features</Eyebrow>
                            <h2 className="text-3xl font-bold tracking-tight text-hx-text sm:text-4xl">The Hexlink Advantage</h2>
                        </div>

                        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                            {features.map((feature) => (
                                <div
                                    key={feature.name}
                                    className={`h-full rounded-2xl p-7 border transition-colors ${
                                        feature.highlight
                                            ? 'bg-gradient-to-br from-hx-accent/15 to-hx-panel border-hx-accent/50'
                                            : 'bg-hx-panel border-hx-line hover:border-hx-line2'
                                    }`}
                                >
                                    <div className="inline-flex p-3 rounded-xl bg-hx-accent/10 border border-hx-accent/25 text-hx-accent">
                                        <feature.icon className="h-6 w-6" />
                                    </div>
                                    <h3 className="mt-4 text-lg font-semibold text-hx-text">{feature.name}</h3>
                                    <p className="mt-2 text-hx-muted">{feature.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Everything included */}
                <section className="py-16 sm:py-20 bg-hx-bg2 border-y border-hx-line">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-10">
                            <Eyebrow>Included</Eyebrow>
                            <h2 className="text-3xl font-bold tracking-tight text-hx-text">Everything Included</h2>
                            <p className="mt-4 text-lg text-hx-muted">Your Hexlink membership includes:</p>
                        </div>
                        <div className="rounded-2xl bg-hx-panel border border-hx-line p-6 sm:p-8">
                            <div className="grid sm:grid-cols-2 gap-4">
                                {benefits.map((benefit) => (
                                    <div key={benefit} className="flex items-start">
                                        <CheckCircleIcon className="h-6 w-6 text-hx-accent mt-0.5 mr-3 flex-shrink-0" />
                                        <span className="text-hx-text">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Lineup & plans */}
                <section className="py-16 sm:py-24">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <Eyebrow>The lineup</Eyebrow>
                            <h2 className="text-3xl font-bold tracking-tight text-hx-text sm:text-4xl">Choose Your Hexlink</h2>
                            <p className="mt-4 max-w-2xl mx-auto text-lg text-hx-muted">
                                Same NFC chip, same profile. Every card adds a one-time $10 Basic setup.
                            </p>
                        </div>

                        <div className="mt-12 grid gap-5 sm:grid-cols-3">
                            {lineup.map((item) => (
                                <a
                                    key={item.name}
                                    href={item.href}
                                    className={`relative flex flex-col rounded-2xl p-7 bg-hx-panel transition-all hover:-translate-y-1 ${
                                        item.badge
                                            ? 'border border-hx-accent shadow-[0_0_0_1px_#35d6ff,0_20px_60px_rgba(53,214,255,0.12)]'
                                            : 'border border-hx-line hover:border-hx-accent'
                                    }`}
                                >
                                    {item.badge && (
                                        <span className="absolute -top-3 left-6 inline-flex px-2.5 py-1 rounded-md font-techmono text-[11px] font-bold uppercase tracking-wider bg-hx-laser text-[#1c0a00]">
                                            {item.badge}
                                        </span>
                                    )}
                                    <h3 className="text-lg font-semibold text-hx-text">{item.name}</h3>
                                    <p className="mt-2 text-hx-muted sm:min-h-[3rem]">{item.description}</p>
                                    <div className="mt-6 text-4xl font-bold tracking-tight text-hx-text">{item.price}</div>
                                    <div className="mt-1 font-techmono text-xs text-hx-dim">+ $10 one-time setup</div>
                                    <div className="mt-6 inline-flex items-center font-semibold text-hx-accent">
                                        {item.cta}
                                        <ArrowRightIcon className="ml-2 w-4 h-4" />
                                    </div>
                                </a>
                            ))}
                        </div>

                        <div className="mt-6 grid gap-5 sm:grid-cols-2">
                            <a href="https://nordic3d.us/hexlink.html#plans" className="block rounded-2xl p-6 bg-hx-panel border border-hx-line hover:border-hx-accent transition-colors">
                                <div className="font-techmono text-xs uppercase tracking-wider text-hx-laser">Coming soon</div>
                                <h3 className="mt-1 text-lg font-semibold text-hx-text">Hexlink Pro</h3>
                                <p className="mt-1 text-hx-muted">Custom themes, advanced analytics and lead capture. $4.99/mo or $39/yr.</p>
                            </a>
                            <a href="https://nordic3d.us/business.html" className="block rounded-2xl p-6 bg-hx-panel border border-hx-line hover:border-hx-accent transition-colors">
                                <div className="font-techmono text-xs uppercase tracking-wider text-hx-ok">For organizations</div>
                                <h3 className="mt-1 text-lg font-semibold text-hx-text">Team &amp; Non-Profit</h3>
                                <p className="mt-1 text-hx-muted">Branded cards for your whole team, with bulk pricing. Non-profits pay once, with no monthly bill.</p>
                            </a>
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="pb-20 sm:pb-24">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="rounded-3xl border border-hx-line2 bg-gradient-to-br from-[#0d2733] via-[#0c1219] to-[#24130a] px-6 py-14 sm:px-12 sm:py-16 text-center">
                            <h2 className="text-3xl font-bold tracking-tight text-hx-text sm:text-4xl">
                                <span className="block">Ready to join the Hexlink network?</span>
                                <span className="block mt-2 text-hx-accent">Get your card today.</span>
                            </h2>
                            <p className="mt-4 text-lg text-hx-muted">Tap once. Share everything. Update anytime.</p>
                            <div className="mt-8">
                                <a
                                    href="https://nordic3d.us/hexlink.html"
                                    className="inline-flex items-center px-7 py-3.5 rounded-xl text-base font-semibold text-hx-ink bg-hx-accent hover:bg-hx-accent2 hover:shadow-[0_0_30px_rgba(53,214,255,0.35)] transition-all"
                                >
                                    Get Your Hexlink Today
                                    <ArrowRightIcon className="ml-2 w-5 h-5" />
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
}

export default Home;
